import React from "react";
import { Box, Stack, Text, Flex, BoxProps } from "@chakra-ui/core";
import { ChevronDown, ChevronUp } from "lucide-react";

import { DAYS_IN_ORDER, NextOpening } from "../utils/availableHours";

import { useStoreOpen, useTenant } from "~/tenant/hooks";
import { useTranslation } from "~/i18n/hooks";

const buildOpensAtText = (
  t: ReturnType<typeof useTranslation>,
  next: NextOpening
): string => {
  if (next.offset === 0) {
    return t("schedule.opensToday", { time: next.time });
  }
  if (next.offset === 1) {
    return t("schedule.opensTomorrow", { time: next.time });
  }
  return t("schedule.opensDay", {
    day: t(`schedule.days.${next.day}`),
    time: next.time,
  });
};

const StoreScheduleStatus: React.FC<BoxProps> = (props) => {
  const t = useTranslation();
  const { availableHours } = useTenant();
  const { isOpen, nextOpening, hasSchedule } = useStoreOpen();
  const [expanded, setExpanded] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!expanded) return;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (
        containerRef.current &&
        target &&
        !containerRef.current.contains(target)
      ) {
        setExpanded(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [expanded]);

  if (!hasSchedule || !availableHours) return null;

  const statusLabel = isOpen ? t("schedule.openNow") : t("schedule.closed");
  const dotColor = isOpen ? "green.500" : "red.500";
  const textColor = isOpen ? "successText" : "dangerText";
  const opensAt =
    !isOpen && nextOpening ? buildOpensAtText(t, nextOpening) : null;

  const todayIndex = new Date().getDay();

  return (
    <Box ref={containerRef} position="relative" {...props}>
      <Flex
        alignItems="center"
        cursor="pointer"
        display="inline-flex"
        onClick={() => setExpanded((e) => !e)}
        role="button"
      >
        <Box
          backgroundColor={dotColor}
          borderRadius="full"
          flexShrink={0}
          height={2}
          marginRight={2}
          width={2}
        />
        <Text
          color={textColor}
          fontSize={{ base: "sm", sm: "md" }}
          fontWeight="500"
        >
          {statusLabel}
        </Text>
        {opensAt && (
          <Text
            color="subtle"
            fontSize={{ base: "sm", sm: "md" }}
            marginLeft={2}
          >
            · {opensAt}
          </Text>
        )}
        <Box as="span" color="subtle" marginLeft={1} lineHeight={0}>
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </Box>
      </Flex>
      {expanded && (
        <Stack
          backgroundColor="card"
          borderColor="borderStrong"
          borderRadius="md"
          borderWidth="1px"
          boxShadow="md"
          left={0}
          marginTop={2}
          minWidth="280px"
          paddingX={4}
          paddingY={3}
          position="absolute"
          spacing={2}
          top="100%"
          zIndex={4}
        >
          {DAYS_IN_ORDER.map((day, index) => {
            const daySchedule = availableHours.schedule.find(
              (d) => d.day === day
            );
            const ranges =
              daySchedule?.enabled && daySchedule.ranges?.length
                ? daySchedule.ranges
                    .map((r) => `${r.open} - ${r.close}`)
                    .join(", ")
                : null;
            const isToday = index === todayIndex;
            return (
              <Flex
                key={day}
                fontSize="sm"
                fontWeight={isToday ? "600" : "400"}
                justifyContent="space-between"
              >
                <Text
                  color="strong"
                  marginRight={4}
                  textTransform="capitalize"
                >
                  {t(`schedule.days.${day}`)}
                </Text>
                <Text color={ranges ? "strong" : "faint"}>
                  {ranges ?? t("schedule.tenantClosed")}
                </Text>
              </Flex>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export default StoreScheduleStatus;
